var __defProp = Object.defineProperty;
var __defProps = Object.defineProperties;
var __getOwnPropDescs = Object.getOwnPropertyDescriptors;
var __getOwnPropSymbols = Object.getOwnPropertySymbols;
var __hasOwnProp = Object.prototype.hasOwnProperty;
var __propIsEnum = Object.prototype.propertyIsEnumerable;
var __defNormalProp = (obj, key, value) => key in obj ? __defProp(obj, key, { enumerable: true, configurable: true, writable: true, value }) : obj[key] = value;
var __spreadValues = (a, b) => {
  for (var prop in b || (b = {}))
    if (__hasOwnProp.call(b, prop))
      __defNormalProp(a, prop, b[prop]);
  if (__getOwnPropSymbols)
    for (var prop of __getOwnPropSymbols(b)) {
      if (__propIsEnum.call(b, prop))
        __defNormalProp(a, prop, b[prop]);
    }
  return a;
};
var __spreadProps = (a, b) => __defProps(a, __getOwnPropDescs(b));
var __async = (__this, __arguments, generator) => {
  return new Promise((resolve, reject) => {
    var fulfilled = (value) => {
      try {
        step(generator.next(value));
      } catch (e) {
        reject(e);
      }
    };
    var rejected = (value) => {
      try {
        step(generator.throw(value));
      } catch (e) {
        reject(e);
      }
    };
    var step = (x) => x.done ? resolve(x.value) : Promise.resolve(x.value).then(fulfilled, rejected);
    step((generator = generator.apply(__this, __arguments)).next());
  });
};

// src/hooks/useI18n.ts
import {
  DATE_FORMAT_STYLE_DMY,
  DATE_FORMAT_STYLE_MDY,
  DATE_FORMAT_STYLE_YMD
} from "@gettactic/api";
var dictionary = {
  [DATE_FORMAT_STYLE_DMY]: {
    displayDate: "dd/MM/yyyy",
    displayDateShort: "dd/MM/yy",
    displayDateWordy: "eeee, LLLL d",
    displayMonthDayYearWordy: "do MMM, yyyy",
    displayMonthDayWordy: "do LLL"
  },
  [DATE_FORMAT_STYLE_MDY]: {
    displayDate: "MM/dd/yyyy",
    displayDateShort: "MM/dd/yy",
    displayDateWordy: "eeee, LLLL do",
    displayMonthDayYearWordy: "MMM do, yyyy",
    displayMonthDayWordy: "LLL do"
  },
  [DATE_FORMAT_STYLE_YMD]: {
    displayDate: "yyyy-MM-dd",
    displayDateShort: "yy-MM-dd",
    displayDateWordy: "LLLL eeee do",
    displayMonthDayYearWordy: "yyyy do, MMM",
    displayMonthDayWordy: "do LLL"
  }
};
function useI18n(user) {
  var _a, _b, _c, _d;
  const dateFormat = user && "user" in user ? (_c = (_b = (_a = user == null ? void 0 : user.user) == null ? void 0 : _a.organization_user) == null ? void 0 : _b.date_format) != null ? _c : DATE_FORMAT_STYLE_MDY : (_d = user == null ? void 0 : user.date_format) != null ? _d : DATE_FORMAT_STYLE_MDY;
  const templates = dictionary[dateFormat];
  return { templates };
}

// src/providers/TacticHooksProvider.tsx
import { QueryClientProvider } from "@tanstack/react-query";
import * as React from "react";
import { jsx } from "react/jsx-runtime";
var TacticHooksContext = React.createContext(null);
var TacticHooksProvider = ({
  apiClient,
  queryClient,
  children
}) => /* @__PURE__ */ jsx(
  TacticHooksContext.Provider,
  {
    value: { apiClient, queryClient, api: apiClient },
    children: /* @__PURE__ */ jsx(QueryClientProvider, { client: queryClient, children })
  }
);
function useTacticHooks() {
  const { apiClient, queryClient, api } = React.useContext(TacticHooksContext);
  if (apiClient === void 0 || queryClient === void 0) {
    throw new Error("useTacticHooks must be used within a TacticHooksProvider");
  }
  return {
    apiClient: apiClient.client,
    queryClient,
    api
  };
}

// src/useOrganizationQuery/index.ts
import { useQuery } from "@tanstack/react-query";
var useOrganizationQuery = () => {
  const { apiClient } = useTacticHooks();
  return useQuery(
    ["users.organizations"],
    () => __async(void 0, null, function* () {
      const res = yield apiClient.users.organizations();
      return res.result;
    }),
    {
      staleTime: 5e3 * 60
      // 5 minutes
    }
  );
};

// src/hooks/useAllApplicablePolicies.ts
import { useQuery as useQuery2, useQueryClient } from "@tanstack/react-query";
import { useCallback } from "react";
var USE_LEAVE_POLICIES_QUERY_KEY = "leavePolicies";
function useAllApplicablePolicies(params, options = { keepPreviousData: true, enabled: true }) {
  const queryClient = useQueryClient();
  const { apiClient } = useTacticHooks();
  const query = useQuery2(
    [USE_LEAVE_POLICIES_QUERY_KEY, params],
    () => __async(this, null, function* () {
      const res = yield apiClient.leave.allApplicablePolicies(params);
      return res.filter((x) => !!x && !!x.policy);
    }),
    options
  );
  const invalidate = useCallback(() => {
    queryClient.invalidateQueries([USE_LEAVE_POLICIES_QUERY_KEY]);
  }, []);
  return { query, invalidate };
}
function useApplicableRemotePolicy(params, options) {
  var _a, _b;
  const { query } = useAllApplicablePolicies(__spreadProps(__spreadValues({}, params), { type: ["remote"] }), options);
  return (_b = (_a = query.data) == null ? void 0 : _a.find((x) => {
    var _a2;
    return ((_a2 = x == null ? void 0 : x.policy) == null ? void 0 : _a2.policy_type) === "remote";
  })) != null ? _b : null;
}

// src/hooks/useAttendanceRequests.tsx
import { parseDateOnlyString } from "@gettactic/api";
import { useQuery as useQuery3 } from "@tanstack/react-query";
import { addDays, eachDayOfInterval, format } from "date-fns";
import { useMemo } from "react";
function useAttendanceRequests(params, options = { keepPreviousData: true, enabled: true }) {
  const { apiClient } = useTacticHooks();
  const commonParams = __spreadProps(__spreadValues({}, params), {
    start: params.start ? format(params.start, "yyyy-MM-dd") : void 0,
    end: params.end ? format(params.end, "yyyy-MM-dd") : void 0,
    workPolicyType: params.workPolicyType ? params.workPolicyType : [],
    status: params.status ? params.status : ["pending", "accepted"]
  });
  const dataQuery = useQuery3(
    ["attendanceRequests", commonParams],
    () => __async(this, null, function* () {
      const res = yield apiClient.leave.allRequests(commonParams);
      return res.result ? res.result.elements : [];
    }),
    options
  );
  return dataQuery;
}
function useAttendanceRequestsWeek(monday, tz) {
  const attendanceQuery = useAttendanceRequests({
    start: monday,
    end: addDays(monday, 7),
    tz
  });
  const attendanceByDay = useMemo(() => {
    var _a;
    const requests = (_a = attendanceQuery.data) != null ? _a : [];
    const weekDayRequests = {};
    requests.forEach((request) => {
      eachDayOfInterval({
        start: parseDateOnlyString(request.starts),
        end: parseDateOnlyString(request.ends)
      }).forEach((date) => {
        const current = weekDayRequests[format(date, "yyyy-MM-dd")];
        if (!current || current.starts > request.starts) {
          weekDayRequests[format(date, "yyyy-MM-dd")] = request;
        }
      });
    });
    return weekDayRequests;
  }, [attendanceQuery.data]);
  return { attendanceQuery, attendanceByDay };
}

// src/hooks/useWorkingLocations.ts
import { useInfiniteQuery } from "@tanstack/react-query";
import { useMemo as useMemo2 } from "react";
function useWorkingLocations(filters, options = { keepPreviousData: true, enabled: true }, key = "") {
  const { apiClient } = useTacticHooks();
  const standardizedFilters = useMemo2(() => {
    return __spreadProps(__spreadValues({}, filters), {
      limit: filters.limit || 100
    });
  }, [JSON.stringify(filters)]);
  const { data, error, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, status } = useInfiniteQuery(
    [key, "schedules", "workingLocations", standardizedFilters],
    (_0) => __async(this, [_0], function* ({ pageParam: cursor }) {
      const queryFilters = __spreadValues({}, standardizedFilters);
      if (cursor) {
        queryFilters.cursor = cursor;
      }
      try {
        const res = yield apiClient.schedules.workingLocations(queryFilters);
        return res.result;
      } catch (error2) {
        return {
          days: {},
          cursor: null
        };
      }
    }),
    {
      getNextPageParam: (lastPage) => {
        var _a, _b, _c;
        const all = (_a = lastPage == null ? void 0 : lastPage.days) != null ? _a : {};
        const days = Object.keys(all);
        let maxLength = 0;
        const lastCursor = lastPage == null ? void 0 : lastPage.cursor;
        for (let i = 0; i < days.length; i++) {
          const day = days[i];
          const dayData = [].concat((_b = all[day]) != null ? _b : []);
          const dayLength = dayData.length;
          if (dayLength > maxLength) {
            maxLength = dayLength;
          }
        }
        return maxLength < ((_c = standardizedFilters.limit) != null ? _c : 0) ? void 0 : lastCursor != null ? lastCursor : void 0;
      },
      keepPreviousData: options.keepPreviousData
    }
  );
  return { data, error, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage, status };
}

// src/hooks/useDivisionsMenuQuery.ts
import { useQuery as useQuery4 } from "@tanstack/react-query";
function byOfficeIdBuilder(divisionsTree) {
  const map = /* @__PURE__ */ new Map();
  const visitNode = (node) => {
    if ("offices" in node) {
      for (const office of node.offices) {
        map.set(office.id, node);
      }
    }
    if ("children" in node) {
      for (const child of node.children) {
        visitNode(child);
      }
    }
  };
  if (Array.isArray(divisionsTree)) {
    for (const node of divisionsTree) {
      visitNode(node);
    }
  } else {
    visitNode(divisionsTree);
  }
  return map;
}
var useDivisionsMenuQuery = (options) => {
  const { apiClient } = useTacticHooks();
  return useQuery4(
    ["useDivisionsMenuQuery", apiClient.slug, options == null ? void 0 : options.getFloors],
    () => __async(void 0, null, function* () {
      const res = yield apiClient.divisions.withOffices(
        void 0,
        options == null ? void 0 : options.getFloors
      );
      const result = res.result;
      return result ? {
        tree: result,
        byOfficeId: byOfficeIdBuilder(result)
      } : void 0;
    }),
    {
      staleTime: 5e3 * 60,
      enabled: !!apiClient.slug
    }
  );
};

// src/hooks/useDivisionsQuery.ts
import { useQuery as useQuery5 } from "@tanstack/react-query";
import { useMemo as useMemo3 } from "react";
var useDivisionsQuery = (filter = {}) => {
  const { apiClient } = useTacticHooks();
  const divisions = useQuery5(
    ["useDivisionsQuery", apiClient.slug, filter],
    () => __async(void 0, null, function* () {
      const res = yield apiClient.divisions.all(filter);
      return res.result;
    }),
    {
      enabled: !!apiClient.slug
    }
  );
  const computed = useMemo3(() => {
    var _a, _b, _c;
    const hasDivisions = ((_b = (_a = divisions.data) == null ? void 0 : _a.length) != null ? _b : 0) > 0;
    const divisionsOptions = ((_c = divisions.data) != null ? _c : []).map((x) => ({
      label: x.name,
      parent_id: x.parent_id,
      value: x.id
    }));
    divisionsOptions.unshift({
      label: "-- No division --",
      value: "",
      parent_id: ""
    });
    return {
      hasDivisions,
      divisionsOptions
    };
  }, [divisions.data]);
  return __spreadValues({
    divisions
  }, computed);
};

// src/hooks/useOfficesQuery.ts
import { useQuery as useQuery6 } from "@tanstack/react-query";
var useOfficesQuery = () => {
  const { apiClient } = useTacticHooks();
  return useQuery6(
    ["useOfficesQuery", apiClient.slug],
    () => __async(void 0, null, function* () {
      const res = yield apiClient.organizations.offices();
      return res.result;
    }),
    {
      staleTime: 5e3 * 60,
      enabled: !!apiClient.slug
    }
  );
};

// src/hooks/usePageSectionsQuery.ts
import { useQuery as useQuery7 } from "@tanstack/react-query";
var usePageSectionsQuery = (filter) => {
  const { apiClient } = useTacticHooks();
  const sections = useQuery7(
    ["usePageSectionsQuery", apiClient.slug, filter],
    () => __async(void 0, null, function* () {
      const res = yield apiClient.pages.getSections();
      return res.result;
    }),
    {
      enabled: !!apiClient.slug
    }
  );
  return sections;
};

// src/hooks/usePagesQuery.ts
import { useQuery as useQuery8 } from "@tanstack/react-query";
var usePagesQuery = (filter) => {
  const { apiClient } = useTacticHooks();
  const sections = useQuery8(
    ["usePageQuery", apiClient.slug, filter],
    () => __async(void 0, null, function* () {
      const res = yield apiClient.pages.getPages();
      return res.result;
    }),
    {
      enabled: !!apiClient.slug
    }
  );
  return sections.data;
};

// src/hooks/tags/useGetAmenities.ts
import { useQuery as useQuery9 } from "@tanstack/react-query";
var useGetAmenities = () => {
  const { apiClient } = useTacticHooks();
  return useQuery9(
    ["tags", "amenities"],
    () => __async(void 0, null, function* () {
      const res = yield apiClient.tags.getTags({
        type: "amenity"
      });
      return res;
    }),
    {
      enabled: !!apiClient.slug
    }
  );
};

// src/providers/AppAuthenticatedProvider.tsx
import { getDeviceTZ } from "@gettactic/helpers/src/intl/getDeviceTZ";
import Cookies from "js-cookie";
import React2, { useEffect, useReducer } from "react";
import { jsx as jsx2 } from "react/jsx-runtime";
var AppAuthenticatedContext = React2.createContext(void 0);
function userContextReducer(state, action) {
  var _a, _b;
  switch (action.type) {
    case "updateAuthUser": {
      return __spreadProps(__spreadValues({}, state), {
        authenticatedUser: action.payload
      });
    }
    case "updateCurrentOffice": {
      const officeMatch = (((_b = (_a = state.authenticatedUser) == null ? void 0 : _a.offices) == null ? void 0 : _b.offices) || []).find((x) => x.id === action.payload);
      if (!officeMatch) {
        throw new Error("Office not found");
      }
      Cookies.set("__selected__office", action.payload);
      return __spreadProps(__spreadValues({}, state), {
        currentOffice: officeMatch
      });
    }
    case "updateDeviceTz": {
      return __spreadProps(__spreadValues({}, state), {
        deviceTz: action.payload
      });
    }
    default: {
      throw new Error(`Unhandled action type: ${action}`);
    }
  }
}
var AppAuthenticatedProvider = ({ authenticatedUser, children, api, cookieOffice: forceOffice }) => {
  var _a, _b, _c, _d, _e, _f, _g, _h, _i;
  useEffect(() => {
    action({ type: "updateAuthUser", payload: authenticatedUser });
  }, [authenticatedUser]);
  if (authenticatedUser) {
    api.updateUser(authenticatedUser);
  }
  const cookieOffice = forceOffice || Cookies.get("__selected__office");
  const selectedCookieOffice = (_a = authenticatedUser == null ? void 0 : authenticatedUser.offices) == null ? void 0 : _a.offices.find(
    (id) => id.id === cookieOffice
  );
  const defaultOrFirstOffice = selectedCookieOffice || (((_b = authenticatedUser == null ? void 0 : authenticatedUser.offices) == null ? void 0 : _b.officeId) ? (_d = (_c = authenticatedUser == null ? void 0 : authenticatedUser.offices) == null ? void 0 : _c.offices) == null ? void 0 : _d.find(
    (x) => {
      var _a2;
      return x.id === ((_a2 = authenticatedUser == null ? void 0 : authenticatedUser.offices) == null ? void 0 : _a2.officeId);
    }
  ) : (authenticatedUser == null ? void 0 : authenticatedUser.offices) ? authenticatedUser == null ? void 0 : authenticatedUser.offices.office : (_e = authenticatedUser == null ? void 0 : authenticatedUser.offices) == null ? void 0 : _e.offices[0]);
  if (!defaultOrFirstOffice) {
    throw new Error("No office found");
  }
  const [state, action] = useReducer(userContextReducer, {
    authenticatedUser,
    deviceTz: (_i = (_h = (_f = getDeviceTZ()) != null ? _f : defaultOrFirstOffice.time_zone) != null ? _h : (_g = authenticatedUser == null ? void 0 : authenticatedUser.user) == null ? void 0 : _g.time_zone) != null ? _i : "UTC",
    currentOffice: defaultOrFirstOffice
  });
  const value = {
    userContext: state,
    setUserContext: action
  };
  return /* @__PURE__ */ jsx2(AppAuthenticatedContext.Provider, { value, children });
};

// src/hooks/authenticated/useAuthenticated.ts
import React3 from "react";
function useAuthenticated() {
  const context = React3.useContext(AppAuthenticatedContext);
  if (context === void 0) {
    throw new Error(
      "useAuthenticated must be used within a AppAuthenticatedContext.Provider"
    );
  }
  return context;
}

// src/hooks/authenticated/useAuthenticatedCurrentOffice.ts
function useAuthenticatedCurrentOffice() {
  const { userContext } = useAuthenticated();
  return userContext.currentOffice;
}

// src/hooks/authenticated/useAuthenticatedUser.ts
function useAuthenticatedUser() {
  const { userContext } = useAuthenticated();
  return userContext.authenticatedUser;
}

// src/hooks/resources/useResources.ts
import { useQuery as useQuery10 } from "@tanstack/react-query";
function useResources({
  officeId,
  types = ["workspace", "desk", "meeting_room"],
  parentId,
  filter,
  query,
  hierarchy = false,
  manage = false,
  limit = 4e3
}, {
  enabled = true,
  coreApi = false
} = {
  enabled: true,
  coreApi: false
}) {
  const { api } = useTacticHooks();
  types.sort();
  const { data: resources, error } = useQuery10(
    [
      "organizations.resourcesAll",
      officeId,
      types,
      parentId ? parentId : null,
      filter ? filter : null,
      void 0,
      null,
      query,
      void 0,
      limit,
      hierarchy,
      manage
    ],
    () => __async(this, null, function* () {
      if (coreApi) {
        const res2 = yield api.client.organizations.getAllResources({
          officeId,
          types,
          parentId: parentId ? parentId : null,
          filter: filter ? filter : null,
          query,
          limit,
          hierarchy,
          manage
        });
        return res2.result;
      }
      const res = yield api.client.organizations.resourcesAll(
        officeId,
        types,
        parentId ? parentId : null,
        filter ? filter : null,
        void 0,
        null,
        query,
        void 0,
        limit,
        hierarchy,
        manage
      );
      return res.result;
    }),
    {
      enabled: enabled && officeId !== null
    }
  );
  const loading = !resources && !error;
  return { resources, error, loading };
}

// src/hooks/resources/useOfficeResources.ts
import { getStorageFloor } from "@gettactic/helpers/src/resources/getStorageFloor";
import { fillSchedules } from "@gettactic/helpers/src/schedules/fillSchedules";
import { useQuery as useQuery13 } from "@tanstack/react-query";
import { differenceInMinutes, format as format2 } from "date-fns";
import { useEffect as useEffect2, useMemo as useMemo6, useState } from "react";

// src/hooks/schedules/useMapSchedulable.ts
import { useQuery as useQuery11 } from "@tanstack/react-query";
function useMapSchedulable(options) {
  const {
    officeId,
    parentId,
    startDate: startDateWithTimeStr,
    endDate: endDateWithTimeStr,
    duration,
    hierarchy,
    enabled = true,
    coreApi = false,
    tags,
    tags_operator,
    query
  } = options;
  const { api } = useTacticHooks();
  const {
    data,
    error,
    isLoading: loading,
    refetch
  } = useQuery11(
    [
      "organizations.schedulable",
      officeId,
      parentId,
      startDateWithTimeStr,
      duration,
      tags,
      tags_operator,
      coreApi,
      query
    ],
    () => __async(this, null, function* () {
      var _a, _b;
      const types = [
        "desk",
        "workspace",
        "meeting_room",
        "parking_lot",
        "parking_space"
      ];
      if (coreApi) {
        const res2 = yield api.client.organizations.getSchedulableResources({
          officeId,
          parentId,
          startDate: startDateWithTimeStr,
          endDate: endDateWithTimeStr,
          hierarchy,
          limit: 4e3,
          types,
          tags,
          tags_operator,
          query
        });
        return {
          available: ((_a = res2.result) == null ? void 0 : _a.available) || [],
          nonAvailable: ((_b = res2.result) == null ? void 0 : _b.nonAvailable) || []
        };
      }
      const res = yield api.client.organizations.schedulable(
        officeId,
        types,
        parentId,
        [startDateWithTimeStr],
        duration ? duration.toString() : null,
        query,
        4e3,
        hierarchy
      );
      return {
        available: res.result,
        nonAvailable: []
      };
    }),
    {
      enabled: enabled && !!startDateWithTimeStr && !!officeId && (!!duration || coreApi)
    }
  );
  return {
    schedulable: (data == null ? void 0 : data.available) || [],
    nonAvailable: (data == null ? void 0 : data.nonAvailable) || [],
    error,
    loading,
    refetch
  };
}

// src/hooks/users/useUserListPaginated.ts
import { useInfiniteQuery as useInfiniteQuery2 } from "@tanstack/react-query";
import { useMemo as useMemo4 } from "react";
function useUserListPaginated(query, limit, filters = {}, roles = [], managed = false) {
  const { api } = useTacticHooks();
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(filters)) {
    if (v !== null && typeof v !== "undefined") {
      params.set(k, v);
    }
  }
  if (query && query.trim() !== "") {
    params.set("query", query);
  }
  params.set("limit", limit.toString());
  roles.forEach((role) => params.append("role", role));
  const {
    data,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status
  } = useInfiniteQuery2(
    ["organizations.usersPaginated", params.toString(), managed],
    (_0) => __async(this, [_0], function* ({ pageParam: cursor }) {
      if (cursor) {
        params.set("cursor", cursor);
      }
      const res = yield api.client.organizations.usersPaginated(
        params,
        managed
      );
      return res.result;
    }),
    {
      getNextPageParam: (lastPage) => {
        var _a, _b;
        return ((_a = lastPage == null ? void 0 : lastPage.elements.length) != null ? _a : 0) < limit ? void 0 : (_b = lastPage == null ? void 0 : lastPage.cursor) != null ? _b : void 0;
      }
    }
  );
  const allPages = useMemo4(
    () => ((data == null ? void 0 : data.pages) || []).map((x) => {
      var _a;
      return (_a = x == null ? void 0 : x.elements) != null ? _a : [];
    }).reduce((arr, els) => {
      return arr.concat(els);
    }, []),
    [data]
  );
  return {
    data,
    allPages,
    error,
    fetchNextPage,
    hasNextPage,
    isFetching,
    isFetchingNextPage,
    status
  };
}

// src/hooks/resources/useFloors.ts
import { useMemo as useMemo5 } from "react";

// src/hooks/resources/useAreas.ts
import { useQuery as useQuery12 } from "@tanstack/react-query";
function useAreas(officeId, parentId = null) {
  const { api } = useTacticHooks();
  const {
    data: areas,
    error,
    isLoading,
    refetch: refetchAreas
  } = useQuery12(
    ["organizations.resourcesAll", officeId != null ? officeId : "", ["area"], parentId != null ? parentId : ""],
    () => __async(this, null, function* () {
      const res = yield api.client.organizations.resourcesAll(
        officeId != null ? officeId : "",
        ["area"],
        parentId
      );
      return res.result;
    }),
    {
      enabled: officeId !== null,
      keepPreviousData: true
    }
  );
  const loading = isLoading;
  return { areas, error, loading, refetch: refetchAreas };
}

// src/hooks/resources/useFloors.ts
function useFloors(officeId, parentId = null) {
  const { areas, refetch, loading } = useAreas(officeId, parentId);
  const floors = useMemo5(
    () => (areas == null ? void 0 : areas.elements) ? areas.elements.filter((x) => x.is_available) : void 0,
    [areas]
  );
  return { floors, refetch, loading };
}

// src/hooks/resources/useOfficeResources.ts
function useOfficeResources(options) {
  var _a, _b, _c;
  const {
    officeId,
    startDate,
    endDate,
    filterQuery,
    entireOffice,
    getAllMaps,
    _lastFloorId,
    tags,
    tags_operator,
    floorId
  } = options;
  const { api } = useTacticHooks();
  const {
    userContext: { currentOffice: contextSelectedOffice, authenticatedUser }
  } = useAuthenticated();
  const currentOffice = (officeId ? (_b = (_a = authenticatedUser == null ? void 0 : authenticatedUser.offices) == null ? void 0 : _a.byId) == null ? void 0 : _b[officeId] : null) || contextSelectedOffice;
  const [initialFloorId, setInitialFloorId] = useState(options.initialFloorId);
  const startDateWithTimeStr = startDate ? format2(startDate, "yyyy-MM-dd'T'HH:mm:ss'Z'") : null;
  const ebdDateWithTimeStr = endDate ? format2(endDate, "yyyy-MM-dd'T'HH:mm:ss'Z'") : null;
  const startDateStr = startDate ? format2(startDate, "yyyy-MM-dd") : null;
  const calculatedDuration = endDate && startDate ? differenceInMinutes(endDate, startDate) : null;
  const duration = calculatedDuration !== null && calculatedDuration > 0 ? calculatedDuration : null;
  let loading;
  const paginatedUsers = useUserListPaginated(filterQuery, 200);
  loading = !paginatedUsers.data;
  const { floors: allFloors } = useFloors(officeId, null);
  const [floor, setFloor] = useState(null);
  const loadFloor = () => {
    var _a2, _b2, _c2, _d, _e, _f, _g, _h, _i;
    if (!allFloors) {
      return;
    }
    if (allFloors.length === 1) {
      setFloor(allFloors[0]);
      return;
    }
    const lastFloorId = _lastFloorId || getStorageFloor();
    const withMap = getAllMaps ? allFloors : allFloors.filter((x) => !!x.map_url || !!x.map_data);
    const newFloor = (options == null ? void 0 : options.allowNoFloor) ? (
      // If the `allowNoFloor` option is set, only consider the user selection without default fallbacks
      (_c2 = (_b2 = (_a2 = withMap.find((x) => x.id === initialFloorId)) != null ? _a2 : withMap.find((x) => x.id === (floor == null ? void 0 : floor.id))) != null ? _b2 : withMap.find((x) => x.id === lastFloorId)) != null ? _c2 : null
    ) : (
      // If the `allowNoFloor` option is set, try to find the best match
      (_i = (_h = (_g = (_f = (_e = (_d = withMap.find((x) => x.id === initialFloorId)) != null ? _d : withMap.find((x) => x.id === floorId)) != null ? _e : withMap.find((x) => x.id === lastFloorId)) != null ? _f : withMap.find((x) => x.id === authenticatedUser.defaultAreaId)) != null ? _g : withMap.find((x) => x.id === (floor == null ? void 0 : floor.id))) != null ? _h : withMap[0]) != null ? _i : allFloors[0]
    );
    if ((floor == null ? void 0 : floor.id) !== (newFloor == null ? void 0 : newFloor.id) || (floor == null ? void 0 : floor.map_data) !== (newFloor == null ? void 0 : newFloor.map_data)) {
      if (initialFloorId) setInitialFloorId(null);
      setFloor(newFloor);
    }
  };
  useEffect2(loadFloor, [
    allFloors,
    floor == null ? void 0 : floor.id,
    authenticatedUser,
    currentOffice.id,
    floorId
  ]);
  loading || (loading = typeof allFloors === "undefined");
  const floorExists = !!(allFloors == null ? void 0 : allFloors.find(
    (x) => x.id === (floor == null ? void 0 : floor.id) && floor.office_id === currentOffice.id
  ));
  const enableFetch = floorExists;
  const params = {
    office: officeId ? [officeId] : [],
    office_id: officeId || "",
    date: startDateStr != null ? startDateStr : "",
    type: ["desk", "workspace", "meeting_room", "parking_lot", "parking_space"],
    tz: currentOffice.time_zone,
    days: 1,
    resource: !entireOffice && (floor == null ? void 0 : floor.id) ? [floor == null ? void 0 : floor.id] : void 0
  };
  const { data: usersSchedule, refetch: refetchSchedules } = useQuery13(
    ["schedules", "occupied", params],
    () => __async(this, null, function* () {
      const res = yield api.client.schedules.allIndexed(
        "occupied",
        params,
        options.coreApi || false
      );
      return res.result;
    }),
    {
      enabled: enableFetch && officeId !== null
    }
  );
  loading || (loading = floorExists && !usersSchedule);
  const floorsWithMap = allFloors ? getAllMaps ? allFloors : allFloors.filter((x) => x.map_url) : void 0;
  const { resources } = useResources(
    {
      officeId,
      types: [
        "desk",
        "workspace",
        "meeting_room",
        "parking_lot",
        "parking_space"
      ],
      parentId: !entireOffice ? floor == null ? void 0 : floor.id : void 0,
      hierarchy: !entireOffice
      // query: filterQuery,
    },
    {
      enabled: enableFetch && officeId !== null,
      coreApi: options.coreApi
    }
  );
  loading || (loading = floorExists && !resources);
  const { schedulable, refetch: refetchMApSchedulable } = useMapSchedulable({
    officeId,
    parentId: !entireOffice ? (_c = floor == null ? void 0 : floor.id) != null ? _c : null : null,
    startDate: startDateWithTimeStr,
    endDate: ebdDateWithTimeStr,
    duration,
    hierarchy: !entireOffice,
    enabled: enableFetch,
    tags,
    tags_operator,
    coreApi: options.coreApi,
    query: filterQuery
  });
  const reload = () => __async(this, null, function* () {
    yield refetchSchedules();
    yield refetchMApSchedulable();
    loadFloor();
  });
  loading || (loading = floorExists && !schedulable);
  const resourcesFilled = useMemo6(() => {
    if (!resources || !schedulable || !startDateStr) {
      return [];
    }
    return fillSchedules(
      resources.elements.map((r) => {
        const s = (schedulable || []).find((x) => x.id === r.id);
        return s ? __spreadProps(__spreadValues(__spreadValues({}, r), s), { label: r.label, description: r.description }) : __spreadProps(__spreadValues({}, r), { available_slots: 0, is_available: 0 });
      }),
      usersSchedule,
      startDateStr
    );
  }, [resources, schedulable, usersSchedule, startDateStr]);
  const floorsWithResources = useMemo6(() => {
    if (!resourcesFilled) {
      return void 0;
    }
    const list = floorsWithMap != null ? floorsWithMap : [];
    const hasOnlyOneFloor = list.length === 1;
    return list.map((floor2, number) => __spreadProps(__spreadValues({}, floor2), {
      number: number + 1,
      resources: resourcesFilled.filter((r) => hasOnlyOneFloor ? true : r.parent_id === floor2.id)
    }));
  }, [floorsWithMap, resourcesFilled, schedulable]);
  const usersAtOffice = useMemo6(() => {
    if (!resourcesFilled) {
      return void 0;
    }
    return resourcesFilled.reduce((arr, res) => {
      const users = (res.schedules || []).map((sc) => __spreadProps(__spreadValues({}, sc.user), {
        resourceId: sc.resource.id
      }));
      return arr.concat(users);
    }, []);
  }, [resourcesFilled]);
  return {
    loading,
    // use the schedulable data filled with
    // resources in use
    schedulable: resourcesFilled,
    floors: floorsWithMap,
    floorsWithResources,
    floor,
    setFloor,
    usersAtOffice,
    resources: resources ? resources.elements : void 0,
    reload
  };
}

// src/hooks/resources/useFloorsForOffices.ts
import { useQuery as useQuery14 } from "@tanstack/react-query";
function useFloorsForOffices(offices) {
  const { api } = useTacticHooks();
  return useQuery14(
    ["organizations.resourcesAll", "multi-floor", offices],
    () => __async(this, null, function* () {
      const promises = offices.map((office) => __async(this, null, function* () {
        var _a, _b;
        const res = yield api.client.organizations.resourcesAll(office, [
          "area"
        ]);
        return ((_a = res.result) == null ? void 0 : _a.elements) ? (_b = res.result) == null ? void 0 : _b.elements.filter((x) => x.is_available) : [];
      }));
      return (yield Promise.all(promises)).flat();
    })
  );
}

// src/hooks/resources/useResource.ts
import { useQuery as useQuery15 } from "@tanstack/react-query";
var useResource = (resourceId, options) => {
  const { api } = useTacticHooks();
  const { data: resource, error } = useQuery15(
    ["resource", resourceId],
    () => __async(void 0, null, function* () {
      var _a;
      if (!resourceId) return null;
      const res = yield api.client.organizations.resourceGet(resourceId);
      return (_a = res.result) == null ? void 0 : _a.resource;
    }),
    {
      enabled: (options == null ? void 0 : options.enabled) !== false && resourceId !== null
    }
  );
  const loading = !resource && !error;
  return { resource, error, loading };
};

// src/hooks/resources/useHasStations.ts
import { useQuery as useQuery16 } from "@tanstack/react-query";
var useHasStations = () => {
  var _a;
  const { api } = useTacticHooks();
  const hasStationQuery = useQuery16(["stations", "hasStations"], () => __async(void 0, null, function* () {
    const res = yield api.client.stations.hasStations();
    return res.result;
  }));
  const hasStations = !!((_a = hasStationQuery.data) == null ? void 0 : _a.enabled);
  return { hasStations };
};

// src/hooks/resources/useResourceSchedules.ts
import { useQuery as useQuery17 } from "@tanstack/react-query";
function useResourceSchedules(resourceId, office_id, weekDate, timeZone) {
  const { api } = useTacticHooks();
  const {
    setUserContext,
    userContext: { deviceTz }
  } = useAuthenticated();
  const scheduleData = useQuery17(
    ["schedules", resourceId, weekDate, "day", timeZone],
    () => __async(this, null, function* () {
      const res = yield api.client.tableOverview.getTableOverview({
        office_id,
        user_tz: deviceTz
      });
      return res.result;
    })
  );
  return scheduleData;
}

// src/hooks/resources/useResourceEvents.ts
import { useQuery as useQuery18 } from "@tanstack/react-query";
import { format as format3 } from "date-fns";
import { groupBy } from "@gettactic/helpers/src/objects/groupBy";
import { toZonedTime } from "@gettactic/helpers";
function useResourceEvents({
  time: _time,
  officeId,
  tz = "UTC",
  resourceId,
  accountForTZ = true
}) {
  const { api } = useTacticHooks();
  const time = accountForTZ && _time ? toZonedTime(_time, tz) : new Date(_time);
  const yyyyMMdd = _time ? format3(time, "yyyy-MM-dd") : void 0;
  return useQuery18(
    ["schedules", "occupied", officeId, resourceId, yyyyMMdd],
    () => __async(this, null, function* () {
      var _a;
      if (!resourceId || !officeId) return;
      const res = yield api.client.schedules.allIndexed(
        "occupied",
        {
          resource_id: resourceId,
          office_id: officeId,
          date: yyyyMMdd,
          tz
        },
        true
      );
      return Object.values(
        groupBy(
          ((_a = res.result) == null ? void 0 : _a.elements) || [],
          (s) => `${s.resource.id}-${s.id}`
        )
      );
    }),
    {
      refetchInterval: 1e3 * 60 * 5,
      keepPreviousData: true,
      enabled: !!resourceId && !!officeId
    }
  );
}

// src/hooks/schedules/useSortedSchedules.ts
import { sortSchedulesByStart } from "@gettactic/helpers/src/schedules/sortSchedulesByStart";
import React4 from "react";
function useSortedSchedules(data, filter = "all") {
  return React4.useMemo(() => {
    const noFilter = data != null ? data : [];
    return noFilter.filter(
      (x) => filter === "all" ? true : filter === "rooms" && x.resource.type === "meeting_room" || filter === "workspaces" && x.resource.type !== "meeting_room"
    ).sort(sortSchedulesByStart);
  }, [filter, data]);
}

// src/hooks/schedules/useDefaultOfficeHours.ts
import { ALL_WEEK_DAYS } from "@gettactic/api";
import {
  getDefaultsHoursFromOffice,
  calculateEndTimeFromDuration
} from "@gettactic/helpers";
import { calculateHoursDifference } from "@gettactic/helpers/src/date-time/calculateHoursDifference";
import {
  isWithinWorkingHours
} from "@gettactic/helpers/src/schedules/isWithinWorkingHours";
import { useCallback as useCallback2, useMemo as useMemo7 } from "react";

// src/hooks/hours/useOperationalHours.ts
import { useQuery as useQuery19 } from "@tanstack/react-query";
function useOperationalHours(office) {
  const { api } = useTacticHooks();
  return useQuery19(
    ["hours.rules", office == null ? void 0 : office.id],
    () => __async(this, null, function* () {
      var _a;
      const res = yield api.client.hours.weekdays((_a = office == null ? void 0 : office.id) != null ? _a : "");
      return res.result;
    }),
    {
      enabled: !!(office == null ? void 0 : office.id)
    }
  );
}

// src/hooks/schedules/useDefaultOfficeHours.ts
var WEEK_START_ON_MONDAY = Object.freeze([6, 0, 1, 2, 3, 4, 5]);
function useDefaultOfficeHours(office) {
  var _a;
  const { id: officeId } = office;
  const workingHours = useOperationalHours(
    officeId ? { id: officeId } : void 0
  );
  const definedOfficeHour = useMemo7(
    () => getDefaultsHoursFromOffice(office),
    [office == null ? void 0 : office.id, (_a = office == null ? void 0 : office.settings) == null ? void 0 : _a.reservation_default_time]
  );
  const definedOfficeHourWindow = useMemo7(
    () => ({
      startHour: definedOfficeHour.startHour,
      startMinutes: definedOfficeHour.startMinutes,
      endHour: definedOfficeHour.endHour,
      endMinutes: definedOfficeHour.endMinutes
    }),
    [definedOfficeHour]
  );
  return useCallback2(
    (forDay) => {
      var _a2, _b, _c, _d;
      if (!workingHours.isSuccess) {
        return void 0;
      }
      if (workingHours.isFetched && ((_b = (_a2 = workingHours.data) == null ? void 0 : _a2.elements) == null ? void 0 : _b.length) === 0) {
        return definedOfficeHourWindow;
      }
      const operational = (_d = (_c = workingHours.data) == null ? void 0 : _c.elements) != null ? _d : [];
      const dayOfWeekName = ALL_WEEK_DAYS[WEEK_START_ON_MONDAY[forDay.getDay()]];
      const currentWorkingHour = operational.find(
        (x) => {
          var _a3;
          return (_a3 = x.days) == null ? void 0 : _a3.find((xx) => xx.toLowerCase() === dayOfWeekName);
        }
      );
      const workingHoursOpenWindow = currentWorkingHour ? calculateHoursDifference(
        currentWorkingHour.start_time,
        currentWorkingHour.end_time
      ) : 0;
      const isOpen24 = workingHoursOpenWindow === 24;
      if (!currentWorkingHour) {
        return definedOfficeHourWindow;
      }
      const [startHours, startMinutes] = currentWorkingHour.start_time.split(":");
      const operationalStartHour = Number.parseInt(startHours);
      const operationalStartMinutes = Number.parseInt(startMinutes);
      const { endHour: operationalEndHour, endMinutes: operationalEndMinutes } = calculateEndTimeFromDuration(
        operationalStartHour,
        operationalStartMinutes,
        definedOfficeHour.deskDuration
      );
      const currentWHValues = {
        startHour: operationalStartHour,
        startMinutes: operationalStartMinutes,
        endHour: operationalEndHour,
        endMinutes: operationalEndMinutes,
        meetingRoomDuration: definedOfficeHour.meetingRoomDuration,
        deskDuration: definedOfficeHour.deskDuration
      };
      if (!isOpen24 && !isWithinWorkingHours(definedOfficeHourWindow, currentWHValues)) {
        return currentWHValues;
      }
      if (isOpen24) {
        return definedOfficeHourWindow;
      }
      return currentWHValues;
    },
    [
      workingHours.isSuccess,
      officeId,
      definedOfficeHour,
      definedOfficeHourWindow
    ]
  );
}
var setEndTimeWithDayOverflow = (endDate, startDate, endHour, endMinutes) => {
  endDate.setHours(endHour, endMinutes, 0);
  if (endDate.getTime() <= startDate.getTime()) {
    endDate.setDate(endDate.getDate() + 1);
  }
};

// src/hooks/schedules/useRoomReservation.ts
import { useEffect as useEffect3, useMemo as useMemo8, useRef, useState as useState2 } from "react";

// src/hooks/users/useOpenWithIntegrationCheck.tsx
import { useQuery as useQuery20 } from "@tanstack/react-query";
import { useCallback as useCallback3 } from "react";
var useOpenWithIntegrationCheck = ({
  enabled = false,
  refetchInterval = 1e3,
  apiClient
}) => {
  const { refetch, isRefetching } = useQuery20({
    queryKey: ["calendar-auth"],
    queryFn: () => apiClient.getMicrosoftPermissions(),
    enabled,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    refetchInterval
  });
  const getShouldRequestDelegatedAccess = useCallback3(() => __async(void 0, null, function* () {
    var _a;
    if (!enabled) {
      return false;
    }
    const freshData = yield refetch();
    const freshPermissions = (_a = freshData.data) == null ? void 0 : _a.result;
    if (!freshPermissions) {
      return false;
    }
    return freshPermissions.requires_delegated_access && !freshPermissions.has_delegated_access;
  }), [enabled, refetch]);
  return {
    getShouldRequestDelegatedAccess,
    isRefetching
  };
};

// src/hooks/schedules/useRoomReservation.ts
import {
  CalendarSource
} from "@gettactic/api";
import { getMSTwoWayUrl } from "@gettactic/helpers";

// src/defines.ts
var MS_1_MINUTE = 60 * 1e3;
var MS_2_MINUTE = 2 * 60 * 1e3;
var MS_5_MINUTES = 5 * 60 * 1e3;
var MS_10_MINUTES = 10 * 60 * 1e3;

// src/hooks/schedules/useRoomReservation.ts
var useRoomReservation = ({
  isOpen,
  isRoomReservation: mayNeedDelegatedAccess,
  calendar,
  organizationSettings,
  slug,
  integrationsUrl: integrationsUrl2,
  apiClient
}) => {
  const { hasMSCalendar, hasGoogleCalendar } = useMemo8(() => {
    return {
      hasMSCalendar: (calendar == null ? void 0 : calendar.source) === CalendarSource.Microsoft,
      hasGoogleCalendar: (calendar == null ? void 0 : calendar.source) === CalendarSource.Google
    };
  }, [calendar == null ? void 0 : calendar.source]);
  const [requireDelegatedPermission, setRequireDelegatedPermission] = useState2(false);
  const hasCheckedPermissions = useRef(false);
  const msAuthUrl = getMSTwoWayUrl(integrationsUrl2 || "", slug || "");
  const shouldCheckForDelegatedAccess = useMemo8(() => {
    var _a;
    return (_a = organizationSettings == null ? void 0 : organizationSettings.calendar_settings) == null ? void 0 : _a.microsoft_enable_delegated_user_access;
  }, [organizationSettings]);
  const { getShouldRequestDelegatedAccess } = useOpenWithIntegrationCheck({
    enabled: mayNeedDelegatedAccess && shouldCheckForDelegatedAccess,
    apiClient,
    refetchInterval: MS_1_MINUTE
  });
  useEffect3(() => {
    if (!isOpen) {
      setRequireDelegatedPermission(false);
      hasCheckedPermissions.current = false;
      return;
    }
    if (!hasMSCalendar) {
      setRequireDelegatedPermission(false);
      return;
    }
    if (!mayNeedDelegatedAccess) {
      setRequireDelegatedPermission(false);
      return;
    }
    if (hasCheckedPermissions.current) {
      return;
    }
    const checkPermissions = () => __async(void 0, null, function* () {
      if (shouldCheckForDelegatedAccess) {
        const shouldRequestDelegatedAccess = yield getShouldRequestDelegatedAccess();
        setRequireDelegatedPermission(shouldRequestDelegatedAccess);
        hasCheckedPermissions.current = true;
      } else {
        setRequireDelegatedPermission(false);
      }
    });
    checkPermissions();
  }, [
    mayNeedDelegatedAccess,
    shouldCheckForDelegatedAccess,
    getShouldRequestDelegatedAccess,
    isOpen,
    hasMSCalendar
  ]);
  return {
    requireDelegatedPermission,
    msAuthUrl,
    hasMSCalendar,
    hasGoogleCalendar
  };
};

// src/hooks/schedules/useRoomReservationWeb.ts
import { useState as useState3 } from "react";

// src/hooks/useCalendar.ts
import { useQuery as useQuery21 } from "@tanstack/react-query";
function useCalendarByResource(resourceId) {
  const { apiClient } = useTacticHooks();
  const {
    data: calendar,
    error,
    isLoading
  } = useQuery21(
    ["integrations.calendar", resourceId],
    () => __async(this, null, function* () {
      const res = yield apiClient.calendar.getCalendarByResourceId(
        resourceId
      );
      return res.result;
    }),
    {
      enabled: !!resourceId,
      onError: (error2) => {
        return {};
      },
      meta: {
        shouldIgnoreGlobalErrorHandler: true
      }
    }
  );
  return { calendar, isLoading, error };
}

// src/hooks/settings/useOrganizationSettings.ts
import { useQuery as useQuery22 } from "@tanstack/react-query";
var useOrganizationSettings = ({
  id,
  disabled = false,
  refetchInterval = MS_5_MINUTES
  // default to 5 minutes
}) => {
  const { apiClient } = useTacticHooks();
  const {
    userContext: { deviceTz }
  } = useAuthenticated();
  return useQuery22({
    queryKey: ["organization-settings", id],
    queryFn: () => __async(void 0, null, function* () {
      if (!id) {
        return null;
      }
      try {
        const data = yield apiClient.orgOrOfficeSettings.getOrganizationSettings(deviceTz);
        if (data.ok && data.result) {
          return data.result;
        }
        throw data;
      } catch (error) {
        console.error("Error fetching organization settings", error);
        throw error;
      }
    }),
    enabled: !!id && !disabled,
    keepPreviousData: true,
    refetchInterval
  });
};

// src/hooks/schedules/useRoomReservationWeb.ts
var integrationsUrl = process.env.NEXT_PUBLIC_TACTIC_INTEGRATIONS_URL;
var useRoomReservationWeb = ({
  isOpen,
  isRoomReservation: mayNeedDelegatedAccess
}) => {
  const { api } = useTacticHooks();
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const organization = authenticatedUser == null ? void 0 : authenticatedUser.organization;
  const [roomId, setRoomId] = useState3(null);
  const { calendar } = useCalendarByResource(roomId || null);
  const { data: organizationSettings } = useOrganizationSettings({
    id: organization == null ? void 0 : organization.id,
    disabled: !mayNeedDelegatedAccess || !isOpen,
    refetchInterval: 60 * 1e3
    // 1 min
  });
  return __spreadValues({
    onRoomChange: (roomId2) => {
      setRoomId(roomId2);
    }
  }, useRoomReservation({
    isOpen,
    isRoomReservation: mayNeedDelegatedAccess,
    calendar: calendar || null,
    organizationSettings: organizationSettings || null,
    slug: (organization == null ? void 0 : organization.slug) || "",
    integrationsUrl: integrationsUrl || "",
    apiClient: api.client.calendarAuth
  }));
};

// src/hooks/schedules/useAvailableDays.tsx
import { useQuery as useQuery23 } from "@tanstack/react-query";
var defaultWeekDays = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday"
];
var defaultWeekDaysCount = defaultWeekDays.length;
function useAvailableDays(office, options) {
  const { api } = useTacticHooks();
  return useQuery23(
    ["hours.rules", office == null ? void 0 : office.id],
    () => __async(this, null, function* () {
      var _a, _b;
      const res = yield api.client.hours.weekdays((_a = office == null ? void 0 : office.id) != null ? _a : "");
      if (!res.ok) {
        console.warn(`Failed to fetch available days for office ${office == null ? void 0 : office.id}:`, res.error);
        return defaultWeekDays;
      }
      const elements = ((_b = res.result) == null ? void 0 : _b.elements) || [];
      const availableDays = (elements || []).filter((day) => day.type === "weekday" && day.is_available).reduce((acc, day) => {
        if (day.days) {
          day.days.forEach((day2) => {
            if (!acc.includes(day2)) {
              acc.push(day2);
            }
          });
        }
        return acc;
      }, []).sort(
        (a, b) => ALL_WEEK_DAYS2[a] - ALL_WEEK_DAYS2[b]
      );
      if (!Array.isArray(availableDays) || !availableDays.length) {
        console.warn(`useAvailableDays: Expected array but got ${typeof availableDays}, using defaults`);
        return defaultWeekDays;
      }
      const validDays = availableDays.filter(
        (day) => typeof day === "string" && Object.prototype.hasOwnProperty.call(ALL_WEEK_DAYS2, day)
      );
      if (validDays.length !== availableDays.length) {
        console.warn("useAvailableDays: Some invalid days found, filtering to valid ones:", validDays);
        return validDays.length > 0 ? validDays : defaultWeekDays;
      }
      return availableDays;
    }),
    __spreadValues({
      enabled: !!(office == null ? void 0 : office.id) && (options == null ? void 0 : options.enabled)
    }, options)
  );
}
var ALL_WEEK_DAYS2 = {
  monday: 0,
  tuesday: 1,
  wednesday: 2,
  thursday: 3,
  friday: 4,
  saturday: 5,
  sunday: 6
};

// src/hooks/schedules/useMyCalendar.ts
import { useQuery as useQuery24 } from "@tanstack/react-query";
function useMyCalendar(params) {
  const { api } = useTacticHooks();
  return useQuery24(
    ["my-calendar", params.date, params.tz, params.user_id],
    () => __async(this, null, function* () {
      const res = yield api.client.myCalendar.getCalendar(params);
      return res.result;
    }),
    {
      enabled: !!params.tz && !!params.enabled
    }
  );
}

// src/hooks/users/useUserList.ts
import { useQuery as useQuery25 } from "@tanstack/react-query";
function useUserList(query = null, roles = null, cursor = null, managed = false) {
  const { api } = useTacticHooks();
  return useQuery25(
    ["organizations.usersWithOptions", query, roles, cursor, managed],
    () => __async(this, null, function* () {
      const res = yield api.client.organizations.usersWithOptions(
        query,
        roles,
        {
          cursor,
          limit: null
        },
        managed
      );
      return res.result;
    })
  );
}

// src/hooks/users/useUserListByIds.ts
import { useQuery as useQuery26 } from "@tanstack/react-query";
function useUserListByIds(ids) {
  const { api } = useTacticHooks();
  return useQuery26(["organizations.usersWithOptions", { ids }], () => __async(this, null, function* () {
    const res = yield api.client.organizations.usersById(ids);
    return res;
  }));
}

// src/hooks/users/useFavs.ts
import { useState as useState6, useEffect as useEffect4 } from "react";
import { useQueryClient as useQueryClient2 } from "@tanstack/react-query";

// src/hooks/useTeams.ts
import { useQuery as useQuery27 } from "@tanstack/react-query";
function useTeams(params) {
  const {
    data: teamList,
    error,
    isFetching
  } = useQuery27(
    [
      "organizations.teamsWithOptions",
      params.type,
      params.hidden,
      params.query
    ],
    () => __async(this, null, function* () {
      const { type } = params;
      const res = yield params.api.client.teams.all(__spreadProps(__spreadValues({}, params), {
        type
      }));
      return res.result;
    }),
    { enabled: params.type !== "no-fetch" }
  );
  return { teamList, error, isFetching };
}

// src/hooks/useTeamMembers.ts
import { useQuery as useQuery28 } from "@tanstack/react-query";
function useTeamMembers(teamId, type, query = null, api) {
  const {
    data: users,
    error,
    isLoading
  } = useQuery28(
    ["teams.members", type, teamId, query],
    () => __async(this, null, function* () {
      if (!api) {
        throw new Error("API instance must be provided to useTeamMembers hook");
      }
      const res = yield api.client.teams.members({
        id: teamId != null ? teamId : "",
        query,
        type
      });
      return res.result;
    }),
    {
      enabled: !!teamId && !!api
    }
  );
  const loading = !users && !error;
  return { users, error, loading, isLoading };
}

// src/hooks/useUserTeams.tsx
import { useQuery as useQuery29 } from "@tanstack/react-query";
import { useState as useState5 } from "react";

// src/hooks/useToggle.tsx
import { useCallback as useCallback4, useState as useState4 } from "react";
var useToggle = (initialState = false) => {
  const [state, setState] = useState4(initialState);
  const toggle = useCallback4(() => setState((state2) => !state2), []);
  return [state, toggle];
};

// src/hooks/useUserTeams.tsx
function useUserTeamsList(userId, type, api) {
  const {
    data: userTeamList,
    error,
    isLoading
  } = useQuery29(
    ["teams.forUser", type, userId],
    () => __async(this, null, function* () {
      if (!userId) throw new Error("UserId is required");
      if (!api) {
        throw new Error("API instance must be provided to useUserTeamsList hook");
      }
      const res = yield api.client.teams.forUser({ userId, type });
      return res.result;
    }),
    {
      enabled: !!userId && !!api
    }
  );
  return {
    userTeamList,
    error,
    isLoading
  };
}
function useUserTeams(user, initialList, api) {
  const [teamMemberList, setTeamMemberList] = useState5(initialList);
  const { teamList } = useTeams({ type: "directory", hidden: false, api });
  const [displayOnlyAssigned, toggleDisplayOnlyAssigned] = useToggle(
    initialList.length > 0
  );
  const [teamsQuery, setTeamsQuery] = useState5("");
  function isMember(team) {
    return teamMemberList.find((x) => x.id === team.id);
  }
  function addMember(team) {
    if (isMember(team)) {
      return;
    }
    setTeamMemberList((x) => x.concat([team]));
  }
  function removeMember(team) {
    setTeamMemberList((x) => x.filter((x2) => x2.id !== team.id));
  }
  return {
    displayOnlyAssigned,
    toggleDisplayOnlyAssigned,
    teamsQuery,
    setTeamsQuery,
    teamList,
    teamMemberList,
    isMember,
    addMember,
    removeMember
  };
}

// src/hooks/users/useFavs.ts
function useDefaultGroupMembers(api) {
  var _a, _b;
  const queryClient = useQueryClient2();
  const { teamList: groups } = useTeams({ type: "group", hidden: false, api });
  const { users: groupMembers } = useTeamMembers(
    (_b = (_a = groups == null ? void 0 : groups.elements[0]) == null ? void 0 : _a.id) != null ? _b : null,
    "group",
    null,
    api
  );
  const mutateDefaultGroupMembers = () => __async(this, null, function* () {
    queryClient.invalidateQueries({
      queryKey: ["organizations.teamsWithOptions", "group", false]
    });
    queryClient.invalidateQueries({ queryKey: ["teams.members"] });
  });
  return {
    defaultGroupMembers: groupMembers,
    defaultMutateGroupMembers: mutateDefaultGroupMembers
  };
}
function useFavs(user, api, issueSuccessMessage) {
  const queryClient = useQueryClient2();
  const { userTeamList: userGroups } = useUserTeamsList(user.id, "group", api);
  const { teamList: groups } = useTeams({ type: "group", hidden: false, api });
  const { defaultGroupMembers, defaultMutateGroupMembers } = useDefaultGroupMembers(api);
  const mutateAll = () => __async(this, null, function* () {
    queryClient.invalidateQueries({ queryKey: ["teams.forUser"] });
    queryClient.invalidateQueries({
      queryKey: ["organizations.teamsWithOptions", "group", false]
    });
    yield defaultMutateGroupMembers();
  });
  const [open, setOpen] = useState6(false);
  const [popperElement, setPopperElement] = useState6(null);
  const [referenceElement, setReferenceElement] = useState6(null);
  const [arrowElement, setArrowElement] = useState6(null);
  const userHasAnyFav = !!userGroups && userGroups.elements && userGroups.elements.length > 0;
  const isUserInDefaultGroup = !!defaultGroupMembers && !!defaultGroupMembers.elements.find((x) => x.id === user.id);
  const [hasAnyFav, setHasAnyFav] = useState6(userHasAnyFav);
  useEffect4(() => {
    setHasAnyFav(userHasAnyFav);
  }, [userHasAnyFav]);
  const addUserToDefaultGroup = (api2) => __async(this, null, function* () {
    var _a;
    if (!api2) {
      console.error("API instance must be provided to useFavs hook for addUserToDefaultGroup");
      return;
    }
    if (!isUserInDefaultGroup) {
      const defaultGroup = (groups == null ? void 0 : groups.elements.length) ? groups.elements[0] : (_a = yield api2.client.teams.add({
        name: "My Favorites",
        parent_id: null,
        type: "group"
      })) == null ? void 0 : _a.result;
      if (!defaultGroup) {
        throw new Error("Sorry we couldn't save your user");
      }
      yield api2.client.teams.addMember({
        teamId: defaultGroup.id,
        userId: user.id,
        type: "group"
      });
    }
    setHasAnyFav(true);
    yield mutateAll();
    issueSuccessMessage("Added to your favorites!");
  });
  const removeUserFromDefaultGroup = (api2) => __async(this, null, function* () {
    var _a;
    if (!api2) {
      console.error("API instance must be provided to useFavs hook for removeUserFromDefaultGroup");
      return;
    }
    if (!groups) {
      return;
    }
    if (isUserInDefaultGroup) {
      const defaultGroup = ((_a = groups == null ? void 0 : groups.elements) == null ? void 0 : _a.length) > 0 ? groups.elements[0] : null;
      if (!defaultGroup) {
        throw new Error("Error trying to get the default Group");
      }
      issueSuccessMessage("Removed from your favorites!");
      yield api2.client.teams.removeMember({
        teamId: defaultGroup.id,
        userId: user.id,
        type: "group"
      });
    }
    yield mutateAll();
  });
  const addUserToGroup = (group, api2) => __async(this, null, function* () {
    if (!api2) {
      console.error("API instance must be provided to useFavs hook for addUserToGroup");
      return;
    }
    if (!groups) {
      return;
    }
    yield api2.client.teams.addMember({
      teamId: group.id,
      userId: user.id,
      type: "group"
    });
    setHasAnyFav(true);
    yield mutateAll();
    issueSuccessMessage("Added to your favorites!");
  });
  const removeUserFromGroup = (group, api2) => __async(this, null, function* () {
    if (!api2) {
      console.error("API instance must be provided to useFavs hook for removeUserFromGroup");
      return;
    }
    if (!groups) {
      return;
    }
    yield api2.client.teams.removeMember({
      teamId: group.id,
      userId: user.id,
      type: "group"
    });
    yield mutateAll();
    issueSuccessMessage("Removed from your favorites!");
  });
  return {
    open,
    setOpen,
    popperElement,
    setPopperElement,
    referenceElement,
    setReferenceElement,
    arrowElement,
    setArrowElement,
    hasAnyFav,
    removeUserFromDefaultGroup,
    addUserToDefaultGroup,
    addUserToGroup,
    removeUserFromGroup,
    groups,
    userGroups
  };
}

// src/hooks/colleagues/useColleagues.ts
import { useQuery as useQuery30 } from "@tanstack/react-query";
function useColleagues(userId) {
  const { api } = useTacticHooks();
  const {
    data: colleagues,
    error,
    isFetching
  } = useQuery30(["organizations.user.colleagues", userId], () => __async(this, null, function* () {
    const res = yield api.client.organizations.userColleagues(userId);
    return res.result;
  }));
  return { colleagues, error, isFetching };
}

// src/hooks/useIsPrivacyEnabled.ts
import { isHpe } from "@gettactic/helpers/src/features";
import { isOrganizationAdmin } from "@gettactic/helpers/src/features/isOrganizationAdmin";
var useIsPrivacyEnabled = ({ isOrgPrivacyEnabled }) => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const legacyPrivacyEnabled = isHpe(authenticatedUser);
  const isNotAdmin = !isOrganizationAdmin(authenticatedUser);
  return (legacyPrivacyEnabled || isOrgPrivacyEnabled) && isNotAdmin;
};

// src/hooks/pages/useGetPageSectionsForOffice.ts
import { findDivisionPathByOfficeId } from "@gettactic/helpers/src/divisions";
import React5 from "react";
var useGetPageSectionsForOffice = (selectedOfficeId, options) => {
  const { data: divisions } = useDivisionsMenuQuery();
  const { data: sections } = usePageSectionsQuery();
  return React5.useMemo(() => {
    var _a;
    if (!sections || !selectedOfficeId && !(options == null ? void 0 : options.allowEmptyOffice)) {
      return [];
    }
    const pageSectionsRoot = sections || [];
    const divisionPath = findDivisionPathByOfficeId(
      ((_a = divisions == null ? void 0 : divisions.tree) == null ? void 0 : _a.children) || [],
      selectedOfficeId
    );
    const foundSections = [];
    if (pageSectionsRoot.length > 0) {
      pageSectionsRoot.forEach((section) => {
        if ((options == null ? void 0 : options.allowEmptyOffice) && !selectedOfficeId || section.division_id && (divisionPath == null ? void 0 : divisionPath.includes(section.division_id)) || section.office_id && section.office_id === selectedOfficeId) {
          foundSections.push(section);
        }
      });
    }
    if (options == null ? void 0 : options.parentSectionId) {
      return foundSections.filter(
        (section) => section.parent_id === options.parentSectionId
      );
    }
    if (options == null ? void 0 : options.onlyRootSections) {
      const rootSections = foundSections.filter(
        (section) => !section.parent_id
      );
      if (options.childrenAsRootIfSingleRoot && rootSections.length === 1) {
        return foundSections.filter((section) => section.parent_id === foundSections[0].id).map((section) => {
          return __spreadProps(__spreadValues({}, section), {
            parent: rootSections[0]
          });
        });
      }
      return rootSections;
    }
    return foundSections;
  }, [
    sections,
    selectedOfficeId,
    options == null ? void 0 : options.parentSectionId,
    options == null ? void 0 : options.allowEmptyOffice,
    options == null ? void 0 : options.onlyRootSections,
    options == null ? void 0 : options.childrenAsRootIfSingleRoot
  ]);
};

// src/hooks/settings/useOfficeSettings.ts
import { useQuery as useQuery31 } from "@tanstack/react-query";
var useOfficeSettings = ({
  id
}) => {
  const { apiClient } = useTacticHooks();
  return useQuery31({
    queryKey: ["office-settings", id],
    queryFn: () => __async(void 0, null, function* () {
      if (!id) {
        return null;
      }
      try {
        const data = yield apiClient.orgOrOfficeSettings.getOfficeSettings(id);
        if (data.ok && data.result) {
          return data.result;
        }
        throw data;
      } catch (error) {
        console.error("Error fetching office settings", error);
        throw error;
      }
    }),
    enabled: !!id,
    keepPreviousData: true,
    refetchOnWindowFocus: false
  });
};

// src/hooks/settings/updateOrganizationSettings.ts
var updateOrganizationSettings = (api, settings, onSuccess, onError) => __async(void 0, null, function* () {
  try {
    const data = yield api.client.orgOrOfficeSettings.updateOrganizationSettings(
      settings
    );
    if (data.ok && data.result) {
      const result = data.result.reservation_default_time;
      onSuccess();
      return result;
    }
    console.error("Error creating/updating organization settings", data);
    onError();
    return null;
  } catch (error) {
    console.error("Error creating/updating organization settings", error);
    onError();
    throw error;
  }
});

// src/hooks/settings/updateOrganizationDefaultResTimeSettings.ts
var updateOrganizationDefaultResTimeSettings = (api, settings, onSuccess, onError) => __async(void 0, null, function* () {
  try {
    const data = yield api.client.orgOrOfficeSettings.updateOrganizationDefaultReservationTime(
      settings
    );
    if (data.ok && data.result) {
      const result = data.result.reservation_default_time;
      onSuccess();
      return result;
    }
    console.error("Error creating/updating organization settings", data);
    onError();
    return null;
  } catch (error) {
    console.error("Error creating/updating organization settings", error);
    onError();
    throw error;
  }
});

// src/hooks/settings/updateOfficeSettings.ts
var updateOfficeSettings = (api, id, settings, onSuccess, onError) => __async(void 0, null, function* () {
  try {
    const data = yield api.client.orgOrOfficeSettings.updateOfficeDefaultReservationTime(
      id,
      settings
    );
    if (data.ok && data.result) {
      const result = data.result;
      onSuccess();
      return result;
    }
    onError();
    return data;
  } catch (error) {
    onError();
    throw error;
  }
});

// src/hooks/settings/cancelOfficeSettings.ts
import { ApiClientError } from "@gettactic/api";
var cancelOfficeSettings = (api, id, onSuccess, onError) => __async(void 0, null, function* () {
  var _a;
  try {
    const data = yield api.client.orgOrOfficeSettings.cancelCustomOfficeHours(id);
    if (data.ok) {
      onSuccess();
      return;
    }
    console.error("Error cancelling office settings", data);
    onError();
    return;
  } catch (error) {
    if (error instanceof ApiClientError) {
      if (((_a = error == null ? void 0 : error.response) == null ? void 0 : _a.status) === 404) {
        onSuccess();
        return;
      }
    }
    onError();
    throw error;
  }
});

// src/hooks/settings/cancelOrganizationSettings.ts
var cancelOrganizationSettings = (api, id, onSuccess, onError) => __async(void 0, null, function* () {
  try {
    const data = yield api.client.orgOrOfficeSettings.cancelCustomOrganizationHours();
    if (data.ok && data.result) {
      const result = data.result;
      onSuccess();
      return result;
    }
    console.error("Error cancelling organization settings", data);
    onError();
    return data;
  } catch (error) {
    console.error("Error cancelling organization settings", error);
    onError();
    throw error;
  }
});

// src/hooks/settings/useIsDesignSystem2.ts
import { isDesignSystem2 } from "@gettactic/helpers/src/isDesignSystem2";

// src/hooks/settings/useSettings.tsx
import React6 from "react";
var SettingContext = React6.createContext(
  void 0
);
var useSettings = () => {
  const context = React6.useContext(SettingContext);
  if (!context) {
    throw new Error("useSettings must be used within a SettingsProvider");
  }
  return context;
};

// src/hooks/settings/useIsDesignSystem2.ts
var useIsDesignSystem2 = () => {
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const { designSystem } = useSettings();
  if (!authenticatedUser) return false;
  return isDesignSystem2(authenticatedUser, designSystem);
};

// ../helpers/src/permissions/index.ts
function canScheduleOthers(authenticatedUser) {
  var _a, _b;
  return !!((_a = authenticatedUser == null ? void 0 : authenticatedUser.permissions) == null ? void 0 : _a.schedule_others) || !!((_b = authenticatedUser == null ? void 0 : authenticatedUser.permissions) == null ? void 0 : _b.schedule_others_managed);
}

// src/hooks/settings/useOrgFeatureFlag.ts
var useOrgFeatureFlag = (key) => {
  var _a, _b, _c, _d;
  const {
    userContext: { authenticatedUser }
  } = useAuthenticated();
  const organizationId = (_a = authenticatedUser == null ? void 0 : authenticatedUser.organization) == null ? void 0 : _a.id;
  const { data: organizationSettings } = useOrganizationSettings({
    id: organizationId
  });
  if (key === "tessa") {
    const isAdmin = canScheduleOthers(authenticatedUser);
    return ((_b = organizationSettings == null ? void 0 : organizationSettings.feature_flags) == null ? void 0 : _b.tessa) ? isAdmin : false;
  }
  return (_d = (_c = organizationSettings == null ? void 0 : organizationSettings.feature_flags) == null ? void 0 : _c[key]) != null ? _d : null;
};

// src/hooks/settings/useWorkplaceRequestsAllowed.ts
var useWorkplaceRequestsAllowed = (key) => {
  const workplaceRequestsFeatureFlag = useOrgFeatureFlag("workplace_requests");
  const shouldShow = !!workplaceRequestsFeatureFlag;
  const isV2 = useIsDesignSystem2();
  return shouldShow && isV2;
};

// src/hooks/settings/useAccountSettings.ts
import { useMutation, useQuery as useQuery32, useQueryClient as useQueryClient3 } from "@tanstack/react-query";
var useAccountSettings = (organizationId) => {
  const { apiClient } = useTacticHooks();
  const queryClient = useQueryClient3();
  const query = useQuery32({
    queryKey: ["account-settings", organizationId],
    queryFn: () => __async(void 0, null, function* () {
      if (!organizationId) {
        return null;
      }
      try {
        const data = yield apiClient.orgOrOfficeSettings.getAccountSettings();
        if (data.ok && data.result) {
          return data.result;
        }
        throw data;
      } catch (error) {
        console.error("Error fetching account settings", error);
        throw error;
      }
    }),
    enabled: !!organizationId
  });
  const mutation = useMutation({
    mutationFn: (settings) => __async(void 0, null, function* () {
      const data = yield apiClient.orgOrOfficeSettings.updateAccountSettings(settings);
      if (data.ok && data.result) {
        return data.result;
      }
      throw data;
    }),
    onSuccess: () => {
      queryClient.invalidateQueries(["account-settings", organizationId]);
    }
  });
  return { query, mutation };
};

// src/hooks/agenda/useMyAgenda.tsx
import { useQuery as useQuery33 } from "@tanstack/react-query";
var useMyAgenda = ({
  from,
  tz,
  user_tz
}) => {
  const { apiClient } = useTacticHooks();
  return useQuery33({
    queryKey: ["myAgenda", tz, from],
    queryFn: () => __async(void 0, null, function* () {
      const response = yield apiClient.agenda.getMyAgenda({
        tz,
        user_tz,
        from
      });
      if (response.ok) {
        return response.result;
      }
      return null;
    }),
    enabled: !!tz,
    keepPreviousData: true
  });
};

// src/hooks/favorites/useGetUserFavorites.ts
import { useQuery as useQuery34 } from "@tanstack/react-query";
var useGetUserFavorites = () => {
  const { apiClient } = useTacticHooks();
  return useQuery34(
    ["favorites"],
    () => __async(void 0, null, function* () {
      return yield apiClient.favorites.getFavorites();
    }),
    {
      enabled: !!apiClient.slug
    }
  );
};

// ../helpers/src/date-time/dateParseISO.ts
import { parseDateOnlyString as parseDateOnlyString2 } from "@gettactic/api";

// ../helpers/src/date-time/formatStartEndHours.ts
import { format as format5 } from "date-fns";

// ../helpers/src/date-time/formatHour.ts
import { format as format4 } from "date-fns";

// ../helpers/src/date-time/getFirstMondayBefore.ts
import { format as format6, parse, startOfWeek } from "date-fns";

// ../helpers/src/date-time/getTimeFromSchedule.ts
import { format as format7, parseISO } from "date-fns";

// ../helpers/src/date-time/calculateEndTimeFromDuration.ts
import { addHours, getHours, getMinutes, setHours, setMinutes } from "date-fns";

// ../helpers/src/date-time/index.ts
import { parse as parse2 } from "date-fns";

// ../../node_modules/.pnpm/date-fns-tz@3.2.0_date-fns@4.1.0/node_modules/date-fns-tz/dist/esm/format/index.js
import { format as dateFnsFormat } from "date-fns/format";

// ../../node_modules/.pnpm/date-fns-tz@3.2.0_date-fns@4.1.0/node_modules/date-fns-tz/dist/esm/_lib/tzIntlTimeZoneName/index.js
import { getDefaultOptions } from "date-fns";

// ../../node_modules/.pnpm/date-fns-tz@3.2.0_date-fns@4.1.0/node_modules/date-fns-tz/dist/esm/_lib/tzTokenizeDate/index.js
var testDateFormatted = new Intl.DateTimeFormat("en-US", {
  hourCycle: "h23",
  timeZone: "America/New_York",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit"
}).format(/* @__PURE__ */ new Date("2014-06-25T04:00:00.123Z"));

// ../../node_modules/.pnpm/date-fns-tz@3.2.0_date-fns@4.1.0/node_modules/date-fns-tz/dist/esm/format/formatters/index.js
var MILLISECONDS_IN_MINUTE = 60 * 1e3;

// ../helpers/src/hooks/useBooleanState.ts
import React7 from "react";
var useBooleanState = (initialState = false) => {
  const [state, setState] = React7.useState(initialState);
  const handleSetAsTrue = React7.useCallback(() => {
    setState(true);
  }, []);
  const handleSetAsFalse = React7.useCallback(() => {
    setState(false);
  }, []);
  const toggle = React7.useCallback(() => {
    setState((value) => !value);
  }, []);
  return {
    state,
    handleSetAsTrue,
    handleSetAsFalse,
    true: handleSetAsTrue,
    false: handleSetAsFalse,
    toggle,
    setState
  };
};

// ../helpers/src/hooks/useCallbackRef.ts
import { useCallback as useCallback5, useEffect as useEffect5, useRef as useRef2 } from "react";

// ../helpers/src/hooks/useStandardApiHandler.ts
import { useState as useState7 } from "react";

// ../helpers/src/hooks/useDisclosure.ts
import React9 from "react";

// ../helpers/src/features/getDefaultsHoursFromOffice.ts
import { DEFAULT_TIME_SETTINGS } from "@gettactic/api";

// ../helpers/src/resources/getCurrentUserCanEdit.ts
import { isPast } from "date-fns";

// src/hooks/favorites/useSaveResourceFavorite.ts
var useSaveResourceFavorite = () => {
  const { api } = useTacticHooks();
  const isLoading = useBooleanState(false);
  const { queryClient } = useTacticHooks();
  const {
    userContext: { currentOffice }
  } = useAuthenticated();
  const invalidateQueries = (resourceId) => __async(void 0, null, function* () {
    yield queryClient.invalidateQueries([
      "organizations.resourcesAll",
      currentOffice.id
    ]);
    if (resourceId)
      yield queryClient.invalidateQueries(["resource", resourceId]);
  });
  const saveResourceFavorite = (resourceId) => __async(void 0, null, function* () {
    try {
      const result = yield api.client.favorites.addResourceFavorite(resourceId);
      yield invalidateQueries(resourceId);
      isLoading.false();
      return result;
    } catch (error) {
      console.error("Error saving resource favorite:", error);
      throw error;
    } finally {
      yield invalidateQueries(resourceId);
      isLoading.false();
    }
  });
  const toggleResourceFavorite = (resource) => __async(void 0, null, function* () {
    if (!resource.id) {
      throw new Error("Resource ID is required to toggle favorite status");
    }
    if (resource.favorite_id) {
      try {
        const result = yield api.client.favorites.deleteFavorite(
          resource.favorite_id
        );
        yield invalidateQueries(resource.id);
        isLoading.false();
        return result;
      } catch (error) {
        console.error("Error removing resource favorite:", error);
        throw error;
      } finally {
        yield invalidateQueries(resource.id);
        isLoading.false();
      }
    }
    try {
      const result = yield api.client.favorites.addResourceFavorite(
        resource.id
      );
      yield invalidateQueries(resource.id);
      isLoading.false();
      return result;
    } catch (error) {
      console.error("Error adding resource favorite:", error);
      throw error;
    } finally {
      yield invalidateQueries(resource.id);
      isLoading.false();
    }
  });
  return {
    saveResourceFavorite,
    toggleResourceFavorite,
    isLoading: isLoading.state
  };
};

// src/hooks/search/useGlobalSearch.ts
import React10 from "react";
import { useQuery as useQuery35 } from "@tanstack/react-query";
function useGlobalSearch(params, options) {
  const { api } = useTacticHooks();
  const isEnabled = (options == null ? void 0 : options.enabled) !== false;
  const { data, error, isLoading, refetch } = useQuery35(
    [
      "globalSearch",
      params.query,
      params.office_ids,
      params.start_time,
      params.end_time
    ],
    () => __async(this, null, function* () {
      const res = yield api.client.globalSearch.search(params);
      return res.result;
    }),
    {
      keepPreviousData: false,
      enabled: isEnabled,
      staleTime: 1e3 * 60,
      // 1 minute
      cacheTime: 1e3 * 60 * 5
      // 5 minutes
    }
  );
  const processedData = React10.useMemo(() => {
    var _a, _b, _c, _d, _e;
    if (!data) return void 0;
    const schedulesByUser = {};
    const schedulesByResource = {};
    if (data.schedules) {
      for (const schedule of data.schedules) {
        if ((_a = schedule.user) == null ? void 0 : _a.id) {
          if (schedulesByUser[schedule.user.id]) {
            schedulesByUser[schedule.user.id].push(schedule);
          } else {
            schedulesByUser[schedule.user.id] = [schedule];
          }
        }
        if ((_b = schedule.creator) == null ? void 0 : _b.id) {
          if (schedulesByUser[schedule.creator.id]) {
            schedulesByUser[schedule.creator.id].push(schedule);
          } else {
            schedulesByUser[schedule.creator.id] = [schedule];
          }
        }
        if ((_c = schedule.organizer) == null ? void 0 : _c.id) {
          if (schedulesByUser[schedule.organizer.id]) {
            schedulesByUser[schedule.organizer.id].push(schedule);
          } else {
            schedulesByUser[schedule.organizer.id] = [schedule];
          }
        }
        if (schedule.slots) {
          for (const slot of schedule.slots) {
            if ((_d = slot.user) == null ? void 0 : _d.id) {
              if (schedulesByUser[slot.user.id]) {
                schedulesByUser[slot.user.id].push(schedule);
              } else {
                schedulesByUser[slot.user.id] = [schedule];
              }
            }
          }
        }
        if ((_e = schedule.resource) == null ? void 0 : _e.id) {
          if (schedulesByResource[schedule.resource.id]) {
            schedulesByResource[schedule.resource.id].push(schedule);
          } else {
            schedulesByResource[schedule.resource.id] = [schedule];
          }
        }
      }
    }
    const hasNoResults = (!data.resources || data.resources.length === 0) && (!data.users || data.users.length === 0) && (!data.schedules || data.schedules.length === 0);
    return __spreadProps(__spreadValues({}, data), {
      schedulesByUser,
      schedulesByResource,
      hasNoResults
    });
  }, [data]);
  return {
    data: processedData,
    error,
    loading: isLoading,
    isLoading,
    refetch
  };
}

// src/hooks/useDebouncedEffect.ts
import { useEffect as useEffect6 } from "react";
function useDebouncedEffect(effect, delay, deps) {
  useEffect6(() => {
    const handler = setTimeout(() => {
      effect();
    }, delay);
    return () => {
      clearTimeout(handler);
    };
  }, [...deps, delay]);
}

// src/hooks/tessa/useGetTessaUserChatList.ts
import { useQuery as useQuery36 } from "@tanstack/react-query";
function useGetTessaUserChatList(options) {
  const { apiClient } = useTacticHooks();
  const {
    data: chats,
    error,
    isFetching
  } = useQuery36(
    ["tessa.chats"],
    () => __async(this, null, function* () {
      const res = yield apiClient.tessa.getUserChatList();
      return res.result;
    }),
    { enabled: (options == null ? void 0 : options.enabled) !== false }
  );
  return { chats, error, isFetching };
}

// src/hooks/tessa/useGetTessaChatMessages.ts
import { useQuery as useQuery37 } from "@tanstack/react-query";
function useGetTessaChatMessages(options) {
  const { apiClient } = useTacticHooks();
  const {
    data: messages,
    error,
    isFetching
  } = useQuery37(
    ["tessa.chat.messages", options.chatId],
    () => __async(this, null, function* () {
      const res = yield apiClient.tessa.getChatMessages(options.chatId);
      return res.result;
    }),
    { enabled: options.enabled !== false }
  );
  return { messages, error, isFetching };
}
export {
  ALL_WEEK_DAYS2 as ALL_WEEK_DAYS,
  AppAuthenticatedContext,
  AppAuthenticatedProvider,
  MS_10_MINUTES,
  MS_1_MINUTE,
  MS_2_MINUTE,
  MS_5_MINUTES,
  SettingContext,
  TacticHooksProvider,
  cancelOfficeSettings,
  cancelOrganizationSettings,
  defaultWeekDays,
  defaultWeekDaysCount,
  setEndTimeWithDayOverflow,
  updateOfficeSettings,
  updateOrganizationDefaultResTimeSettings,
  updateOrganizationSettings,
  useAccountSettings,
  useAllApplicablePolicies,
  useApplicableRemotePolicy,
  useAreas,
  useAttendanceRequests,
  useAttendanceRequestsWeek,
  useAuthenticated,
  useAuthenticatedCurrentOffice,
  useAuthenticatedUser,
  useAvailableDays,
  useCalendarByResource,
  useColleagues,
  useDebouncedEffect,
  useDefaultGroupMembers,
  useDefaultOfficeHours,
  useDivisionsMenuQuery,
  useDivisionsQuery,
  useFavs,
  useFloors,
  useFloorsForOffices,
  useGetAmenities,
  useGetPageSectionsForOffice,
  useGetTessaChatMessages,
  useGetTessaUserChatList,
  useGetUserFavorites,
  useGlobalSearch,
  useHasStations,
  useI18n,
  useIsDesignSystem2,
  useIsPrivacyEnabled,
  useMapSchedulable,
  useMyAgenda,
  useMyCalendar,
  useOfficeResources,
  useOfficeSettings,
  useOfficesQuery,
  useOpenWithIntegrationCheck,
  useOperationalHours,
  useOrgFeatureFlag,
  useOrganizationQuery,
  useOrganizationSettings,
  usePageSectionsQuery,
  usePagesQuery,
  useResource,
  useResourceEvents,
  useResourceSchedules,
  useResources,
  useRoomReservation,
  useRoomReservationWeb,
  useSaveResourceFavorite,
  useSettings,
  useSortedSchedules,
  useTacticHooks,
  useTeamMembers,
  useTeams,
  useUserList,
  useUserListByIds,
  useUserListPaginated,
  useUserTeams,
  useUserTeamsList,
  useWorkingLocations,
  useWorkplaceRequestsAllowed
};
//# sourceMappingURL=index.mjs.map